import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  VStack,
  useToast
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { FaArrowDown, FaArrowLeft, FaArrowRight, FaArrowUp, FaExpand, FaHome, FaSearchMinus, FaSearchPlus, FaStop } from "react-icons/fa";
import { MseLivePlayer } from "../../utils/mseLivePlayer";

const MAX_AUTO_RETRIES = 12;
// Enquanto o botão está pressionado, renova o comando antes do limite de segurança do backend
const MOVE_HEARTBEAT_MS = 2000;

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.35; }
  100% { opacity: 1; }
`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json"
});

const readMessage = async (response, fallback) => {
  try {
    const data = await response.json();
    return data.message || data.error || fallback;
  } catch (error) {
    return fallback;
  }
};

const DIRECTION_PAD = [
  { id: "up_left", label: "Cima e esquerda", icon: <FaArrowUp style={{ transform: "rotate(-45deg)" }} /> },
  { id: "up", label: "Cima", icon: <FaArrowUp /> },
  { id: "up_right", label: "Cima e direita", icon: <FaArrowUp style={{ transform: "rotate(45deg)" }} /> },
  { id: "left", label: "Esquerda", icon: <FaArrowLeft /> },
  { id: "stop", label: "Parar", icon: <FaStop /> },
  { id: "right", label: "Direita", icon: <FaArrowRight /> },
  { id: "down_left", label: "Baixo e esquerda", icon: <FaArrowDown style={{ transform: "rotate(45deg)" }} /> },
  { id: "down", label: "Baixo", icon: <FaArrowDown /> },
  { id: "down_right", label: "Baixo e direita", icon: <FaArrowDown style={{ transform: "rotate(-45deg)" }} /> }
];

const PHASE_BADGES = {
  preparing: { label: "Preparando", color: "gray" },
  connecting: { label: "Conectando", color: "yellow" },
  playing: { label: "AO VIVO", color: "red" },
  stalled: { label: "Sem sinal", color: "orange" },
  error: { label: "Erro", color: "red" },
  closed: { label: "Encerrado", color: "gray" }
};

const LiveViewModal = ({ isOpen, onClose, camera }) => {
  const toast = useToast();
  const [videoElement, setVideoElement] = useState(null);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const retriesRef = useRef(0);
  const retryTimerRef = useRef(null);
  const sessionRef = useRef(0);

  const [phase, setPhase] = useState("preparing");
  const [message, setMessage] = useState("");
  const [canControlPtz, setCanControlPtz] = useState(false);

  const [ptzCaps, setPtzCaps] = useState(null);
  const [ptzLoading, setPtzLoading] = useState(false);
  const [speed, setSpeed] = useState(5);
  const [selectedPreset, setSelectedPreset] = useState("");
  const [showOnvifConfig, setShowOnvifConfig] = useState(false);
  const [onvifForm, setOnvifForm] = useState({ onvif_port: "", username: "", password: "" });
  const [savingOnvif, setSavingOnvif] = useState(false);

  const holdRef = useRef({ direction: null, timer: null });
  const queueRef = useRef(Promise.resolve());
  const lastErrorToastRef = useRef(0);

  const cameraId = camera && camera.id;

  const stopPlayer = useCallback(() => {
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = null;
    if (playerRef.current) {
      playerRef.current.stop();
      playerRef.current = null;
    }
  }, []);

  const startSession = useCallback(async () => {
    if (!cameraId || !videoElement) return;

    stopPlayer();
    const sessionId = sessionRef.current + 1;
    sessionRef.current = sessionId;
    setPhase("preparing");
    setMessage("");

    try {
      const response = await fetch(`/api/live/${cameraId}/info`, { headers: authHeaders() });
      if (sessionRef.current !== sessionId) return;

      if (!response.ok) {
        setPhase("error");
        setMessage(await readMessage(response, "Não foi possível abrir o vídeo ao vivo."));
        return;
      }

      const info = await response.json();
      if (sessionRef.current !== sessionId) return;

      setCanControlPtz(Boolean(info.canControlPtz));

      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const wsUrl = `${protocol}://${window.location.host}${info.wsPath}?ticket=${encodeURIComponent(info.ticket)}`;

      const player = new MseLivePlayer(videoElement, {
        wsUrl,
        onState: (state, detail) => {
          if (sessionRef.current !== sessionId) return;

          if (state === "playing") {
            retriesRef.current = 0;
            setPhase("playing");
            setMessage("");
            return;
          }

          if (state === "connecting") {
            setPhase("connecting");
            return;
          }

          if (state === "closed") {
            setPhase("closed");
            setMessage(detail || "Sessão encerrada.");
            return;
          }

          // H.265 sem transcode não se resolve reconectando — evita o loop 12x.
          if (state === "error" && /H\.265|codecs not matched/i.test(detail || "")) {
            setPhase("error");
            setMessage(detail);
            return;
          }

          // stalled | error: tenta reconectar algumas vezes antes de desistir
          if (retriesRef.current < MAX_AUTO_RETRIES) {
            retriesRef.current += 1;
            setPhase("stalled");
            setMessage(`${detail || "Conexão perdida."} Reconectando (${retriesRef.current}/${MAX_AUTO_RETRIES})...`);
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = setTimeout(() => startSession(), Math.min(2000 * retriesRef.current, 6000));
          } else {
            setPhase("error");
            setMessage(detail || "Não foi possível manter a conexão com a câmera.");
          }
        }
      });

      playerRef.current = player;
      setPhase("connecting");
      player.start();
    } catch (error) {
      if (sessionRef.current !== sessionId) return;
      setPhase("error");
      setMessage("Falha de rede ao abrir o vídeo ao vivo.");
    }
  }, [cameraId, videoElement, stopPlayer]);

  const handleManualReconnect = useCallback(() => {
    retriesRef.current = 0;
    startSession();
  }, [startSession]);

  // Ciclo de vida do vídeo: abre ao exibir o modal, encerra ao fechar/trocar de câmera
  useEffect(() => {
    if (!isOpen || !cameraId || !videoElement) return undefined;

    retriesRef.current = 0;
    startSession();

    return () => {
      sessionRef.current += 1;
      stopPlayer();
    };
  }, [isOpen, cameraId, videoElement, startSession, stopPlayer]);

  // ---------- PTZ ----------

  const enqueue = useCallback((task) => {
    // Move/stop precisam chegar ao backend na ordem em que o usuário os acionou
    queueRef.current = queueRef.current.then(task, task);
    return queueRef.current;
  }, []);

  const showPtzError = useCallback((text) => {
    const now = Date.now();
    if (now - lastErrorToastRef.current < 3000) return;
    lastErrorToastRef.current = now;
    toast({ title: "PTZ", description: text, status: "error", duration: 4000, isClosable: true });
  }, [toast]);

  const postPtz = useCallback(async (path, body) => {
    try {
      const response = await fetch(`/api/live/${cameraId}/ptz/${path}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body || {})
      });
      if (!response.ok) showPtzError(await readMessage(response, "A câmera recusou o comando."));
      return response.ok;
    } catch (error) {
      showPtzError("Falha de rede ao enviar o comando PTZ.");
      return false;
    }
  }, [cameraId, showPtzError]);

  const loadPtzCapabilities = useCallback(async () => {
    if (!cameraId) return;
    setPtzLoading(true);
    try {
      const response = await fetch(`/api/live/${cameraId}/ptz/capabilities`, { headers: authHeaders() });
      if (!response.ok) {
        setPtzCaps({ supported: false, message: await readMessage(response, "Não foi possível consultar o PTZ.") });
        return;
      }
      const caps = await response.json();
      setPtzCaps(caps);
      setOnvifForm((current) => ({ ...current, onvif_port: caps.onvif_port ? String(caps.onvif_port) : "" }));
    } catch (error) {
      setPtzCaps({ supported: false, message: "Falha de rede ao consultar o PTZ." });
    } finally {
      setPtzLoading(false);
    }
  }, [cameraId]);

  useEffect(() => {
    if (isOpen && canControlPtz) loadPtzCapabilities();
  }, [isOpen, canControlPtz, loadPtzCapabilities]);

  const sendStop = useCallback(() => enqueue(() => postPtz("stop")), [enqueue, postPtz]);

  const endMove = useCallback(() => {
    const hold = holdRef.current;
    if (!hold.direction) return;

    clearInterval(hold.timer);
    hold.timer = null;
    hold.direction = null;
    sendStop();
  }, [sendStop]);

  // Botão de emergência: sempre envia "parar", mesmo sem movimento em andamento
  const forceStop = useCallback(() => {
    const hold = holdRef.current;
    clearInterval(hold.timer);
    hold.timer = null;
    hold.direction = null;
    sendStop();
  }, [sendStop]);

  const startMove = useCallback((direction) => {
    const hold = holdRef.current;
    if (hold.direction) return;

    const sendMove = () => enqueue(() => postPtz("move", { direction, speed: speed / 10 }));
    hold.direction = direction;
    sendMove();
    hold.timer = setInterval(sendMove, MOVE_HEARTBEAT_MS);
  }, [enqueue, postPtz, speed]);

  // Nunca deixa a câmera girando ao fechar o modal ou desmontar
  useEffect(() => {
    if (!isOpen) endMove();
    return () => endMove();
  }, [isOpen, endMove]);

  const holdProps = (direction) => ({
    onPointerDown: (event) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      startMove(direction);
    },
    onPointerUp: endMove,
    onPointerCancel: endMove,
    onLostPointerCapture: endMove,
    onContextMenu: (event) => event.preventDefault(),
    style: { touchAction: "none", userSelect: "none" }
  });

  const handleGotoPreset = async () => {
    if (!selectedPreset) return;
    await enqueue(() => postPtz("preset", { token: selectedPreset }));
  };

  const handleHome = () => enqueue(() => postPtz("home"));

  const handleSaveOnvif = async () => {
    setSavingOnvif(true);
    try {
      const payload = { onvif_port: onvifForm.onvif_port, username: onvifForm.username };
      if (onvifForm.password) payload.password = onvifForm.password;

      const response = await fetch(`/api/live/${cameraId}/ptz/config`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        toast({ title: "Não foi possível salvar", description: await readMessage(response, "Erro ao salvar."), status: "error", duration: 4000, isClosable: true });
        return;
      }

      setOnvifForm((current) => ({ ...current, password: "" }));
      toast({ title: "Ajustes ONVIF salvos", status: "success", duration: 2000, isClosable: true });
      loadPtzCapabilities();
    } catch (error) {
      toast({ title: "Falha de rede ao salvar", status: "error", duration: 3000, isClosable: true });
    } finally {
      setSavingOnvif(false);
    }
  };

  const handleFullscreen = () => {
    const element = containerRef.current;
    if (!element) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else if (element.requestFullscreen) element.requestFullscreen();
  };

  const handleClose = () => {
    endMove();
    onClose();
  };

  const badge = PHASE_BADGES[phase] || PHASE_BADGES.preparing;
  const showOverlay = phase !== "playing";
  const isBusy = phase === "preparing" || phase === "connecting" || phase === "stalled";
  const showRetryButton = phase === "error" || phase === "closed";
  const ptzSupported = Boolean(ptzCaps && ptzCaps.supported);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" closeOnOverlayClick={false} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={3} wrap="wrap">
            <Text>Ao vivo — {camera ? camera.name : ""}</Text>
            <Badge colorScheme={badge.color} px={2} py={1} borderRadius="md" display="inline-flex" alignItems="center" gap={2}>
              {phase === "playing" && <Box w="8px" h="8px" borderRadius="full" bg="red.500" animation={`${pulse} 1.4s infinite`} />}
              {badge.label}
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex gap={4} direction={{ base: "column", lg: "row" }} align="stretch">
            <Box flex="1" minW={0}>
              <Box
                ref={containerRef}
                position="relative"
                bg="black"
                borderRadius="md"
                overflow="hidden"
                sx={{ aspectRatio: "16 / 9" }}
              >
                <video
                  ref={setVideoElement}
                  muted
                  autoPlay
                  playsInline
                  style={{ width: "100%", height: "100%", objectFit: "contain", background: "black" }}
                />

                {showOverlay && (
                  <Flex position="absolute" inset={0} direction="column" align="center" justify="center" bg="blackAlpha.700" color="white" textAlign="center" px={6} gap={3}>
                    {isBusy && <Spinner size="xl" thickness="4px" color="teal.300" />}
                    <Text fontWeight="semibold">
                      {phase === "preparing" && "Preparando transmissão..."}
                      {phase === "connecting" && "Conectando à câmera..."}
                      {phase === "stalled" && "Sem sinal"}
                      {phase === "error" && "Não foi possível exibir a câmera"}
                      {phase === "closed" && "Transmissão encerrada"}
                    </Text>
                    {message && <Text fontSize="sm" color="gray.200">{message}</Text>}
                    {showRetryButton && (
                      <Button leftIcon={<RepeatIcon />} colorScheme="teal" onClick={handleManualReconnect}>
                        Tentar novamente
                      </Button>
                    )}
                  </Flex>
                )}
              </Box>
            </Box>

            {canControlPtz && (
              <Box w={{ base: "100%", lg: "290px" }} flexShrink={0} borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
                <Text fontWeight="bold" mb={3}>Controle PTZ</Text>

                {ptzLoading && !ptzCaps && (
                  <HStack color="gray.600"><Spinner size="sm" /><Text fontSize="sm">Consultando a câmera...</Text></HStack>
                )}

                {ptzCaps && !ptzSupported && (
                  <Alert status="warning" borderRadius="md" fontSize="sm" mb={3} alignItems="flex-start">
                    <AlertIcon />
                    <AlertDescription>{ptzCaps.message || "PTZ indisponível para esta câmera."}</AlertDescription>
                  </Alert>
                )}

                {ptzSupported && (
                  <VStack align="stretch" spacing={4}>
                    <Grid templateColumns="repeat(3, 1fr)" gap={2} justifyItems="center">
                      {DIRECTION_PAD.map((item) => (
                        item.id === "stop" ? (
                          <IconButton key={item.id} aria-label={item.label} title={item.label} icon={item.icon} colorScheme="red" variant="outline" onClick={forceStop} />
                        ) : (
                          <IconButton key={item.id} aria-label={item.label} title={item.label} icon={item.icon} colorScheme="teal" variant="outline" {...holdProps(item.id)} />
                        )
                      ))}
                    </Grid>

                    <HStack justify="center" spacing={3}>
                      <IconButton aria-label="Afastar zoom" title="Afastar (segure)" icon={<FaSearchMinus />} colorScheme="purple" variant="outline" {...holdProps("zoom_out")} />
                      <Text fontSize="sm" color="gray.600">Zoom</Text>
                      <IconButton aria-label="Aproximar zoom" title="Aproximar (segure)" icon={<FaSearchPlus />} colorScheme="purple" variant="outline" {...holdProps("zoom_in")} />
                    </HStack>

                    <Box>
                      <Flex justify="space-between"><Text fontSize="sm">Velocidade</Text><Text fontSize="sm" color="gray.600">{speed}/10</Text></Flex>
                      <Slider min={1} max={10} step={1} value={speed} onChange={setSpeed} colorScheme="teal">
                        <SliderTrack><SliderFilledTrack /></SliderTrack>
                        <SliderThumb />
                      </Slider>
                    </Box>

                    <Divider />

                    <Box>
                      <Text fontSize="sm" mb={1}>Posições salvas</Text>
                      {ptzCaps.presets && ptzCaps.presets.length > 0 ? (
                        <HStack>
                          <Select size="sm" placeholder="Selecione" value={selectedPreset} onChange={(event) => setSelectedPreset(event.target.value)} bg="white">
                            {ptzCaps.presets.map((preset) => (
                              <option key={preset.token} value={preset.token}>{preset.name}</option>
                            ))}
                          </Select>
                          <Button size="sm" colorScheme="teal" onClick={handleGotoPreset} isDisabled={!selectedPreset}>Ir</Button>
                        </HStack>
                      ) : (
                        <Text fontSize="xs" color="gray.500">Nenhuma posição salva nesta câmera.</Text>
                      )}
                    </Box>

                    <Button size="sm" leftIcon={<FaHome />} variant="outline" onClick={handleHome}>Posição inicial</Button>
                  </VStack>
                )}

                {ptzCaps && (
                  <Box mt={4}>
                    <Button size="xs" variant="link" colorScheme="blue" onClick={() => setShowOnvifConfig((value) => !value)}>
                      {showOnvifConfig ? "Ocultar ajustes ONVIF" : "Ajustes ONVIF"}
                    </Button>
                    <Collapse in={showOnvifConfig} animateOpacity>
                      <VStack align="stretch" spacing={2} mt={2}>
                        <Text fontSize="xs" color="gray.600">
                          {ptzCaps.backend === "isapi"
                            ? "Esta Hikvision responde pelo ISAPI, não pelo ONVIF. O controle usa o IP e as credenciais do RTSP."
                            : "Por padrão usamos o IP e as credenciais do RTSP. Ajuste só se o ONVIF da câmera usar outra porta ou usuário."}
                          {ptzCaps.host ? ` Host: ${ptzCaps.host}.` : ""}
                        </Text>
                        <SimpleGrid columns={1} spacing={2}>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={0}>Porta ONVIF</FormLabel>
                            <Input size="sm" type="number" placeholder="80" value={onvifForm.onvif_port} onChange={(event) => setOnvifForm((current) => ({ ...current, onvif_port: event.target.value }))} bg="white" />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={0}>Usuário (opcional)</FormLabel>
                            <Input size="sm" value={onvifForm.username} onChange={(event) => setOnvifForm((current) => ({ ...current, username: event.target.value }))} bg="white" autoComplete="off" />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={0}>Senha (opcional)</FormLabel>
                            <Input size="sm" type="password" placeholder="Deixe vazio para manter" value={onvifForm.password} onChange={(event) => setOnvifForm((current) => ({ ...current, password: event.target.value }))} bg="white" autoComplete="new-password" />
                          </FormControl>
                        </SimpleGrid>
                        <Button size="sm" colorScheme="blue" onClick={handleSaveOnvif} isLoading={savingOnvif}>Salvar e testar</Button>
                      </VStack>
                    </Collapse>
                  </Box>
                )}
              </Box>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button leftIcon={<FaExpand />} variant="outline" onClick={handleFullscreen}>Tela cheia</Button>
          <Button leftIcon={<RepeatIcon />} variant="outline" onClick={handleManualReconnect}>Reconectar</Button>
          <Button onClick={handleClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LiveViewModal;
